import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { useAppCtx } from "../contexts/AppContext";
import { PeriodFilter, periodParams } from "../components/PeriodFilter";
import { TrendingUp, TrendingDown, ArrowRight, BadgeCheck, Pencil, RotateCcw, Check, X } from "lucide-react";
import { toast } from "sonner";

function num(n, d = 2) {
    if (n === null || n === undefined || n === "") return "—";
    return Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

const KIND_LABEL = {
    contra: "Contra",
    receipt: "Receipt",
    payment: "Payment",
};

// Realized FX Gain/Loss on Transactions.
// Replaces the old bank-balance "FX Revaluation" tool. The numbers here are
// REALIZED — they only show when an actual conversion happened at a rate
// different from the master rate effective on the transaction date.
export default function FxGainLossPage() {
    const { hasPerm } = useAuth();
    const canPost = hasPerm("journals", "write");
    const { entities, selectedEntity } = useAppCtx();

    const [period, setPeriod] = useState({ from: null, to: null, preset: "this_year" });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [postingMonth, setPostingMonth] = useState(null);
    const [postSegment, setPostSegment] = useState("Voice");

    const load = async () => {
        setLoading(true);
        try {
            const params = { ...periodParams(period) };
            if (selectedEntity && selectedEntity !== "all") params.entity_id = selectedEntity;
            const { data } = await api.get("/reports/fx-realized", { params });
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Load failed");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, selectedEntity]);

    const months = data?.summary || [];
    const rows = data?.rows || [];

    const entityName = useMemo(() => {
        if (!selectedEntity || selectedEntity === "all") return "All entities";
        return entities.find((e) => e.id === selectedEntity)?.name || "Entity";
    }, [entities, selectedEntity]);

    const grandTotal = data?.grand_total_usd || 0;
    const gainTotal = data?.totals?.gain_total || 0;
    const lossTotal = data?.totals?.loss_total || 0;
    const txnCount = data?.totals?.txn_count || 0;

    const postMonth = async (m) => {
        if (!canPost) return;
        if (!window.confirm(`Post the net realized FX gain/loss for ${m.month} to P&L?\n\n` +
            `Net: ${num(m.gain_loss_usd)} USD\n` +
            `(+${num(m.gain_total)} gain  /  ${num(m.loss_total)} loss)\n` +
            `Posts under segment: ${postSegment}\n\nThis can be reversed later.`)) return;
        setPostingMonth(m.month);
        try {
            const { data: r } = await api.post("/reports/fx-realized/post", {
                month: m.month,
                entity_id: (selectedEntity && selectedEntity !== "all") ? selectedEntity : null,
                segment: postSegment,
            });
            toast.success(`Posted FX gain/loss for ${m.month} — Journal ${r.journal_id?.slice(0, 8)}…`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Post failed");
        } finally { setPostingMonth(null); }
    };

    const unpostMonth = async (m) => {
        if (!canPost) return;
        if (!window.confirm(`Reverse the posted FX gain/loss JV for ${m.month}?`)) return;
        setPostingMonth(m.month);
        try {
            await api.delete("/reports/fx-realized/post", {
                params: {
                    month: m.month,
                    entity_id: (selectedEntity && selectedEntity !== "all") ? selectedEntity : undefined,
                },
            });
            toast.success(`Reversed FX gain/loss JV for ${m.month}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Unpost failed");
        } finally { setPostingMonth(null); }
    };

    return (
        <div data-testid="fx-gain-loss-page">
            <PageHeader
                title="FX Gain/Loss"
                eyebrow="Realized FX on Transactions"
                description="Computes the realized USD gain/loss whenever a non-USD transaction was converted at a rate different from the master FX rate effective on the transaction date. Scope: cross-currency Contras + Receipts/Payments with a custom fx_rate."
            />

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <PeriodFilter initialPreset="this_year" onChange={setPeriod}/>
                <div className="text-[11px] uppercase tracking-widest text-slate-500">
                    {entityName}
                </div>
                {loading && <span className="text-[12px] text-slate-500">Loading…</span>}
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="fx-summary-cards">
                <SummaryCard label="Net Gain / Loss" value={grandTotal}
                             tone={grandTotal >= 0 ? "gain" : "loss"}
                             icon={grandTotal >= 0 ? TrendingUp : TrendingDown}
                             testid="fx-summary-net"/>
                <SummaryCard label="Total Gains" value={gainTotal} tone="gain"
                             icon={TrendingUp} testid="fx-summary-gains"/>
                <SummaryCard label="Total Losses" value={lossTotal} tone="loss"
                             icon={TrendingDown} testid="fx-summary-losses"/>
                <SummaryCard label="Transactions" value={txnCount} tone="neutral"
                             icon={BadgeCheck} integer testid="fx-summary-count"/>
            </div>

            {/* Per-month table */}
            <div className="bg-white border border-slate-200 mb-6">
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">By month</div>
                    {canPost && (
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-widest text-slate-500">Post under</span>
                            <Select value={postSegment} onValueChange={setPostSegment}>
                                <SelectTrigger className="rounded-sm h-7 w-28 text-[12px]" data-testid="fx-post-segment-select">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Voice">Voice (USD)</SelectItem>
                                    <SelectItem value="SMS">SMS (EUR)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[12.5px] financial min-w-[800px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                                <th className="px-3 py-2.5">Month</th>
                                <th className="px-3 py-2.5 num">Txns</th>
                                <th className="px-3 py-2.5 num">Gains (USD)</th>
                                <th className="px-3 py-2.5 num">Losses (USD)</th>
                                <th className="px-3 py-2.5 num">Net (USD)</th>
                                <th className="px-3 py-2.5">Status</th>
                                <th className="px-3 py-2.5"/>
                            </tr>
                        </thead>
                        <tbody>
                            {months.length === 0 && (
                                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">No realized FX gain/loss in this period.</td></tr>
                            )}
                            {months.map((m) => (
                                <tr key={m.month} className="border-b border-slate-100" data-testid={`fx-month-${m.month}`}>
                                    <td className="px-3 py-2 font-mono text-[12.5px]">{m.month}</td>
                                    <td className="px-3 py-2 num">{m.txn_count}</td>
                                    <td className="px-3 py-2 num text-emerald-700">{num(m.gain_total)}</td>
                                    <td className="px-3 py-2 num text-rose-700">{num(m.loss_total)}</td>
                                    <td className={`px-3 py-2 num font-semibold ${m.gain_loss_usd >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                                        {num(m.gain_loss_usd)}
                                    </td>
                                    <td className="px-3 py-2">
                                        {m.posted
                                            ? <span className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">posted</span>
                                            : <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">unposted</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {canPost && !m.posted && (
                                            <Button size="sm" className="rounded-sm h-7 bg-[#002FA7] text-white"
                                                    disabled={postingMonth === m.month}
                                                    onClick={() => postMonth(m)}
                                                    data-testid={`fx-post-btn-${m.month}`}>
                                                {postingMonth === m.month ? "Posting…" : "Post to P&L"}
                                            </Button>
                                        )}
                                        {canPost && m.posted && (
                                            <Button size="sm" variant="outline" className="rounded-sm h-7"
                                                    disabled={postingMonth === m.month}
                                                    onClick={() => unpostMonth(m)}
                                                    data-testid={`fx-unpost-btn-${m.month}`}>
                                                {postingMonth === m.month ? "…" : "Reverse"}
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Drill-down table */}
            <div className="bg-white border border-slate-200">
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">Transactions</div>
                    <div className="text-[11px] text-slate-500">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px] financial min-w-[1200px]" data-testid="fx-detail-table">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-3 py-2.5">Date</th>
                            <th className="px-3 py-2.5">Type</th>
                            <th className="px-3 py-2.5">Reference</th>
                            <th className="px-3 py-2.5">Conversion</th>
                            <th className="px-3 py-2.5 num" title="Rate the transaction actually used — expressed as '1 USD = X foreign'">Realized Rate <span className="opacity-50 normal-case">(1 USD = X)</span></th>
                            <th className="px-3 py-2.5 num" title="Master FX rate effective on the transaction date — expressed as '1 USD = X foreign'">Master Rate <span className="opacity-50 normal-case">(1 USD = X)</span></th>
                            <th className="px-3 py-2.5 num">Expected (USD)</th>
                            <th className="px-3 py-2.5 num">Realized (USD)</th>
                            <th className="px-3 py-2.5 num">Gain/Loss (USD)</th>
                        </tr>
                    </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-500">No transactions with realized FX gain/loss in this period.</td></tr>
                            )}
                            {rows.map((r) => (
                                <FxRow key={`${r.kind}-${r.id}`} r={r} canEdit={canPost} onSaved={load}/>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function SummaryCard({ label, value, tone, icon: Icon, integer = false, testid }) {
    const tones = {
        gain: "bg-emerald-50 border-emerald-200 text-emerald-800",
        loss: "bg-rose-50 border-rose-200 text-rose-800",
        neutral: "bg-slate-50 border-slate-200 text-slate-800",
    };
    return (
        <div className={`border rounded-sm px-4 py-3 ${tones[tone] || tones.neutral}`} data-testid={testid}>
            <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-widest opacity-70">{label}</span>
                {Icon ? <Icon size={14} className="opacity-60"/> : null}
            </div>
            <div className="text-[20px] font-semibold financial">
                {integer ? value : num(value)}
                {!integer && <span className="text-[11px] uppercase tracking-widest opacity-60 ml-1.5">USD</span>}
            </div>
        </div>
    );
}

// Drill-down row with an editable Master Rate cell. Saving PUT-s the override
// to /api/reports/fx-realized/{kind}/{id}/master-rate; the parent reloads so
// the recomputed gain/loss & summary refresh together.
function FxRow({ r, canEdit, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(String(r.master_rate ?? ""));
    const [busy, setBusy] = useState(false);
    const overridden = r.master_rate_override !== null && r.master_rate_override !== undefined;

    const begin = () => { setDraft(String(r.master_rate ?? "")); setEditing(true); };
    const cancel = () => { setDraft(String(r.master_rate ?? "")); setEditing(false); };

    const save = async () => {
        const n = Number(draft);
        if (!Number.isFinite(n) || n <= 0) {
            toast.error("Enter a positive number");
            return;
        }
        setBusy(true);
        try {
            await api.put(`/reports/fx-realized/${r.kind}/${r.id}/master-rate`,
                          { master_rate: n });
            toast.success("Master rate updated");
            setEditing(false);
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    const reset = async () => {
        if (!window.confirm("Revert to the historical / master rate?")) return;
        setBusy(true);
        try {
            await api.put(`/reports/fx-realized/${r.kind}/${r.id}/master-rate`,
                          { master_rate: null });
            toast.success("Override cleared");
            setEditing(false);
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    return (
        <tr className="border-b border-slate-100" data-testid={`fx-row-${r.id}`}>
            <td className="px-3 py-2 font-mono">{r.date}</td>
            <td className="px-3 py-2">
                <span className="text-[10px] uppercase tracking-widest bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{KIND_LABEL[r.kind] || r.kind}</span>
            </td>
            <td className="px-3 py-2 font-mono text-[#002FA7]">{r.reference || "—"}</td>
            <td className="px-3 py-2">
                <span className="inline-flex items-center gap-1.5 text-[11.5px]">
                    <span className="font-mono">{num(r.from_amount)} {r.from_currency}</span>
                    <ArrowRight size={11} className="text-slate-400"/>
                    <span className="font-mono">{num(r.to_amount)} {r.to_currency}</span>
                </span>
            </td>
            <td className="px-3 py-2 num font-mono">{num(r.realized_rate, 4)}</td>
            <td className="px-3 py-2">
                {editing ? (
                    <div className="flex items-center justify-end gap-1">
                        <Input
                            type="number"
                            step="0.000001"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            className="h-7 w-28 px-1.5 py-0 text-right font-mono text-[11.5px] rounded-sm"
                            disabled={busy}
                            data-testid={`fx-rate-input-${r.id}`}
                        />
                        <button type="button" onClick={save} disabled={busy}
                                className="text-emerald-700 hover:text-emerald-900 disabled:opacity-40"
                                title="Save" data-testid={`fx-rate-save-${r.id}`}>
                            <Check size={14}/>
                        </button>
                        <button type="button" onClick={cancel} disabled={busy}
                                className="text-slate-500 hover:text-slate-800 disabled:opacity-40"
                                title="Cancel" data-testid={`fx-rate-cancel-${r.id}`}>
                            <X size={14}/>
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center justify-end gap-1.5">
                        <span className={`num font-mono ${overridden ? "text-amber-700" : "text-slate-600"}`}
                              title={overridden ? "Master rate manually overridden — click pencil to edit" : "Master rate from FX history. Click pencil to override."}>
                            {num(r.master_rate, 4)}
                            {overridden && <span className="ml-1 text-[9px] uppercase tracking-widest text-amber-700">overridden</span>}
                        </span>
                        {canEdit && (
                            <>
                                <button type="button" onClick={begin}
                                        className="text-slate-400 hover:text-[#002FA7] transition-colors"
                                        title="Edit master rate" data-testid={`fx-rate-edit-${r.id}`}>
                                    <Pencil size={11}/>
                                </button>
                                {overridden && (
                                    <button type="button" onClick={reset} disabled={busy}
                                            className="text-slate-400 hover:text-rose-700 transition-colors disabled:opacity-40"
                                            title="Reset to historical rate" data-testid={`fx-rate-reset-${r.id}`}>
                                        <RotateCcw size={11}/>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}
            </td>
            <td className="px-3 py-2 num font-mono text-slate-600">{num(r.expected_usd)}</td>
            <td className="px-3 py-2 num font-mono">{num(r.realized_usd)}</td>
            <td className={`px-3 py-2 num font-semibold ${r.gain_loss_usd >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {num(r.gain_loss_usd)}
            </td>
        </tr>
    );
}
